<template>
  <div>
    <b-row>
      <b-col cols="12">
        <!-- Tabs Navigation -->
        <b-card no-body>
          <b-tabs v-model="activeTabIndex" pills card>
            <!-- Doctor Information Tab -->
            <b-tab :title="$t('clinic.Doctor-Information')">
              <b-card-text>
                <!-- Doctor Header Card -->
                <b-card class="doctor-header-card mb-3">
                  <b-row>
                    <b-col
                      cols="12"
                      md="3"
                      class="d-flex align-items-center justify-content-center mb-3 mb-md-0"
                    >
                      <b-avatar
                        size="120"
                        variant="light-primary"
                        :text="avatarText(doctorData.name)"
                        class="doctor-avatar"
                      />
                    </b-col>
                    <b-col cols="12" md="9">
                      <h1 class="mb-1 font-weight-bold doctor-name-large">
                        {{ doctorData.name || "-" }}
                      </h1>

                      <div class="mb-2">
                        <b-badge
                          v-if="doctorData.type === 'surgeon'"
                          variant="light-success"
                          class="mr-50"
                        >
                          <feather-icon icon="UserCheckIcon" size="14" />
                          <span class="ml-50">{{
                            $t("hospital.surgeon")
                          }}</span>
                        </b-badge>
                        <b-badge
                          v-if="doctorData.type === 'anesthetist'"
                          variant="light-info"
                          class="mr-50"
                        >
                          <feather-icon icon="ActivityIcon" size="14" />
                          <span class="ml-50">{{
                            $t("hospital.anesthetist")
                          }}</span>
                        </b-badge>
                      </div>

                      <div class="mt-2">
                        <div class="d-flex align-items-center mb-1">
                          <feather-icon
                            icon="PhoneIcon"
                            size="18"
                            class="text-primary mr-1"
                          />
                          <span class="font-weight-bold mr-1"
                            >{{ $t("clinic.doctors-phone") }}:</span
                          >
                          <span>{{ doctorData.phone || "-" }}</span>
                        </div>
                      </div>
                    </b-col>
                  </b-row>
                </b-card>

                <!-- Statistics Cards -->
                <b-row class="mb-3">
                  <b-col cols="12" sm="6" lg="3" class="mb-2">
                    <b-card class="stats-card stats-card-success">
                      <div
                        class="d-flex justify-content-between align-items-center"
                      >
                        <div>
                          <h6 class="text-muted mb-50">
                            {{ $t("accounting.recive-money") }}
                          </h6>
                          <h2 class="font-weight-bolder mb-0">
                            {{ surgeryStats.received_money_count || 0 }}
                          </h2>
                          <small class="text-success">
                            {{ $t("surgery.surgeries_count") }}
                          </small>
                        </div>
                        <div class="stats-icon">
                          <feather-icon
                            icon="CheckCircleIcon"
                            size="24"
                            class="text-success"
                          />
                        </div>
                      </div>
                    </b-card>
                  </b-col>

                  <b-col cols="12" sm="6" lg="3" class="mb-2">
                    <b-card class="stats-card stats-card-danger">
                      <div
                        class="d-flex justify-content-between align-items-center"
                      >
                        <div>
                          <h6 class="text-muted mb-50">
                            {{ $t("accounting.dosnt-recive-money") }}
                          </h6>
                          <h2 class="font-weight-bolder mb-0">
                            {{ surgeryStats.not_received_money_count || 0 }}
                          </h2>
                          <small class="text-danger">
                            {{
                              formatCurrency(
                                surgeryStats.not_received_money_total || 0
                              )
                            }}
                          </small>
                        </div>
                        <div class="stats-icon">
                          <feather-icon
                            icon="XCircleIcon"
                            size="24"
                            class="text-danger"
                          />
                        </div>
                      </div>
                    </b-card>
                  </b-col>

                  <b-col cols="12" sm="6" lg="3" class="mb-2">
                    <b-card class="stats-card stats-card-primary">
                      <div
                        class="d-flex justify-content-between align-items-center"
                      >
                        <div>
                          <h6 class="text-muted mb-50">
                            {{ $t("accounting.setPriceSurgery") }}
                          </h6>
                          <h2 class="font-weight-bolder mb-0">
                            {{ surgeryStats.total_surgery_prices_count || 0 }}
                          </h2>
                          <small class="text-primary">
                            {{ $t("surgery.surgeries_assigned") }}
                          </small>
                        </div>
                        <div class="stats-icon">
                          <feather-icon
                            icon="DollarSignIcon"
                            size="24"
                            class="text-primary"
                          />
                        </div>
                      </div>
                    </b-card>
                  </b-col>

                  <b-col cols="12" sm="6" lg="3" class="mb-2">
                    <b-card class="stats-card stats-card-info">
                      <div
                        class="d-flex justify-content-between align-items-center"
                      >
                        <div>
                          <h6 class="text-muted mb-50">
                            {{ $t("common.total") }}
                            {{ $t("surgery.surgeries") }}
                          </h6>
                          <h2 class="font-weight-bolder mb-0">
                            {{ surgeryStats.total_surgeries || 0 }}
                          </h2>
                          <small class="text-info">
                            {{ $t("surgery.all_time") }}
                          </small>
                        </div>
                        <div class="stats-icon">
                          <feather-icon
                            icon="ActivityIcon"
                            size="24"
                            class="text-info"
                          />
                        </div>
                      </div>
                    </b-card>
                  </b-col>
                </b-row>

                <!-- Additional Information -->
                <b-row>
                  <b-col cols="12" md="6">
                    <b-card class="info-card">
                      <h5 class="mb-2 font-weight-bold">
                        <feather-icon icon="InfoIcon" size="18" class="mr-50" />
                        {{ $t("clinic.Doctor-Information") }}
                      </h5>
                      <b-list-group flush>
                        <b-list-group-item
                          class="d-flex justify-content-between align-items-center"
                        >
                          <span class="font-weight-bold"
                            >{{ $t("common.name") }}:</span
                          >
                          <span>{{ doctorData.name || "-" }}</span>
                        </b-list-group-item>
                        <b-list-group-item
                          class="d-flex justify-content-between align-items-center"
                        >
                          <span class="font-weight-bold"
                            >{{ $t("clinic.doctors-phone") }}:</span
                          >
                          <span>{{ doctorData.phone || "-" }}</span>
                        </b-list-group-item>
                        <b-list-group-item
                          class="d-flex justify-content-between align-items-center"
                        />
                      </b-list-group>
                    </b-card>
                  </b-col>

                  <b-col cols="12" md="6">
                    <b-card class="info-card">
                      <h5 class="mb-2 font-weight-bold">
                        <feather-icon
                          icon="CalendarIcon"
                          size="18"
                          class="mr-50"
                        />
                        {{ $t("clinic.Doctor-Updates") }}
                      </h5>
                      <b-list-group flush>
                        <b-list-group-item
                          class="d-flex justify-content-between align-items-center"
                        >
                          <span class="font-weight-bold"
                            >{{ $t("clinic.Doctor-Added-At") }}:</span
                          >
                          <span>{{ formatDate(doctorData.created_at) }}</span>
                        </b-list-group-item>
                        <b-list-group-item
                          class="d-flex justify-content-between align-items-center"
                        >
                          <span class="font-weight-bold"
                            >{{
                              $t("clinic.Date-of-Last-Update-Doctor")
                            }}:</span
                          >
                          <span>{{ formatDate(doctorData.updated_at) }}</span>
                        </b-list-group-item>
                        <b-list-group-item
                          class="d-flex justify-content-between align-items-center"
                        />
                      </b-list-group>
                    </b-card>
                  </b-col>
                </b-row>
              </b-card-text>
            </b-tab>

            <!-- Surgery Prices Tab -->

            <!-- Surgeries History Tab -->
            <b-tab :title="$t('common.reports')">
              <DoctorSurgeriesHistory
                v-if="activeTabIndex === 1 && doctorData.id"
                :key="`reports-${doctorData.id}`"
                :doctor-name="doctorData.name"
                @surgery-statistics-updated="updateSurgeryStatistics"
              />
            </b-tab>
            <b-tab :title="$t('accounting.setPriceSurgery')">
              <DoctorSurgeryPrices
                v-if="activeTabIndex === 2 && doctorData.id"
                :key="`prices-${doctorData.id}`"
                :doctor-id="doctorData.id"
                @statistics-updated="updatePriceStatistics"
              />
            </b-tab>
            <b-tab :title="$t('incubator.set_price_nicu')">
              <DoctorNICUPrices
                v-if="activeTabIndex === 3 && doctorData.id"
                :key="`nicu-${doctorData.id}`"
                :doctor-id="doctorData.id"
              />
            </b-tab>
            <b-tab :title="$t('accounting.doctor-invoices')">
              <DoctorInvoices
                v-if="activeTabIndex === 4 && doctorData.id"
                :key="`invoices-${doctorData.id}`"
                :doctor-id="doctorData.id"
                :doctor-name="doctorData.name"
              />
            </b-tab>
          </b-tabs>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>
<script>
import {
  BCard,
  BRow,
  BCol,
  BCardText,
  BTabs,
  BTab,
  BAvatar,
  BBadge,
  BListGroup,
  BListGroupItem,
} from "bootstrap-vue";

import { avatarText } from "@core/utils/filter";
import DoctorSurgeriesHistory from "@/views/hospital/DoctorSurgeriesHistory.vue";
import DoctorSurgeryPrices from "@/views/hospital/DoctorSurgeryPrices.vue";
import DoctorNICUPrices from "@/views/hospital/DoctorNICUPrices.vue";
import DoctorInvoices from "@/views/hospital/DoctorInvoices.vue";

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardText,
    BTabs,
    BTab,
    BAvatar,
    BBadge,
    BListGroup,
    BListGroupItem,

    DoctorSurgeriesHistory,
    DoctorSurgeryPrices,
    DoctorNICUPrices,
    DoctorInvoices,
  },
  data() {
    return {
      loading: true,
      activeTabIndex: 0, // Track active tab index
      params: {
        form: {},
        path: "",
      },
      doctorData: {},
      surgeries: [],
      avatarText,
      surgeryStats: {
        received_money_count: 0,
        not_received_money_count: 0,
        not_received_money_total: 0,
        total_surgery_prices_count: 0,
        total_surgeries: 0,
      },
    };
  },
  mounted() {
    this.getPatientSurgeryInfo();
  },

  methods: {
    async getPatientSurgeryInfo() {
      await this.getPatient(`doctor/${this.$route.params.id}`);
      this.loading = false;

      this.doctorData = this.all_data;
    },

    updateSurgeryStatistics(stats) {
      // Update statistics from DoctorSurgeriesHistory component
      this.surgeryStats.received_money_count = stats.received_money_count;
      this.surgeryStats.not_received_money_count =
        stats.not_received_money_count;
      this.surgeryStats.not_received_money_total =
        stats.not_received_money_total;
      this.surgeryStats.total_surgeries = stats.total_surgeries;
    },

    updatePriceStatistics(stats) {
      // Update statistics from DoctorSurgeryPrices component
      this.surgeryStats.total_surgery_prices_count =
        stats.total_surgery_prices_count;
    },

    formatDate(date) {
      if (!date) return "-";
      const options = {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      };
      return new Date(date).toLocaleDateString(undefined, options);
    },

    formatCurrency(amount) {
      const numAmount = parseFloat(amount || 0);
      const formatted = new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(numAmount);
      return `${formatted} IQD`;
    },
  },
};
</script>

<style lang="scss" scoped>
.doctor-header-card {
  background: linear-gradient(
    118deg,
    rgba(115, 103, 240, 0.05),
    rgba(115, 103, 240, 0.02)
  );
  border: none;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);

  .doctor-avatar {
    box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.15);
    border: 3px solid #fff;
  }
}

.stats-card {
  border: none;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px 0 rgba(34, 41, 47, 0.15);
  }

  .stats-icon {
    background: rgba(115, 103, 240, 0.1);
    border-radius: 50%;
    padding: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.stats-card-primary {
  border-left: 3px solid #7367f0;
}

.stats-card-success {
  border-left: 3px solid #28c76f;
}

.stats-card-danger {
  border-left: 3px solid #ea5455;
}

.stats-card-warning {
  border-left: 3px solid #ff9f43;
}

.stats-card-info {
  border-left: 3px solid #00cfe8;
}

.info-card {
  border: none;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
  height: 100%;

  .list-group-item {
    border-left: none;
    border-right: none;
    padding: 0.75rem 0;

    &:first-child {
      border-top: none;
    }

    &:last-child {
      border-bottom: none;
    }
  }
}

.doctor-name-large {
  font-size: 2.5rem;
  line-height: 1.2;
}
</style>
